import React, { useEffect, useRef, useState } from 'react';
import { t } from 'i18next';
import { Button } from 'src/components/ui/button/button';
import { cn } from 'src/lib/utils';

/** If onRetry never settles, re-enable the button after this long so it cannot stay stuck. */
export const RETRY_SAFETY_REENABLE_MS = 10000;

type ErrorStateWithRetryProps = {
  /** May return a promise (react-query's refetch does); it drives the pending state. */
  onRetry: () => unknown;
  message?: string;
  className?: string;
};

/**
 * Shown INSTEAD of an empty state when a load fails, so a failed fetch is never mistaken for
 * "no data". Same look and retry feedback as the brands-failure panel in RequireBrand.
 *
 * Correct under both react-query behaviours: for a query that already has data, `isError`
 * stays true through a refetch and this panel stays mounted showing 'Retrying...'; for a query
 * that NEVER had data (v4.24.4), a refetch resets `error` to null and `status` to 'loading', so
 * the host may unmount this panel on the first click. Either way the retry promise is guarded
 * (a rejection is not unhandled), the pending state is released when it settles, and a safety
 * timer re-enables the button if onRetry never settles.
 */
export const ErrorStateWithRetry: React.FC<ErrorStateWithRetryProps> = ({
  onRetry,
  message,
  className,
}) => {
  const [isRetrying, setIsRetrying] = useState(false);
  const mountedRef = useRef(true);
  const safetyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (safetyTimerRef.current) clearTimeout(safetyTimerRef.current);
    };
  }, []);

  const release = () => {
    if (safetyTimerRef.current) clearTimeout(safetyTimerRef.current);
    safetyTimerRef.current = null;
    if (mountedRef.current) setIsRetrying(false);
  };

  return (
    <div
      role="alert"
      className={cn(
        'tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-2 tw-py-16 tw-text-center',
        className,
      )}
    >
      <p className="tw-text-lg tw-text-gray-800">{t('Something went wrong')}</p>
      <p className="tw-mb-2 tw-text-sm tw-text-gray-600">
        {message ?? t("We couldn't load this. Please check your connection and try again.")}
      </p>
      <Button
        variant="outline"
        disabled={isRetrying}
        onClick={() => {
          setIsRetrying(true);
          if (safetyTimerRef.current) clearTimeout(safetyTimerRef.current);
          safetyTimerRef.current = setTimeout(release, RETRY_SAFETY_REENABLE_MS);
          // Guarded so a rejection is not unhandled; the host owns showing the failure.
          // finally, not then: a second failure must release the button too.
          Promise.resolve()
            .then(onRetry)
            .catch(() => {})
            .finally(release);
        }}
      >
        {isRetrying ? t('Retrying...') : t('Try again')}
      </Button>
    </div>
  );
};
